import { formatDate } from "../../utils/date.js";
import { invoicePhotoUrl } from "../../services/apiClient.js";
import IssuerLogo from "../shared/IssuerLogo.jsx";
import PaymentTerms from "./PaymentTerms.jsx";

// The GPS tax invoice, rendered from a persisted invoice SNAPSHOT (services/invoices).
// Replaces the legacy Crystal Reports layout — one clean, print-ready document. Issuer
// details are the real GPS ones (from the legacy .rpt); later they move to org settings
// so per-debtor variants (Vero etc.) can key off the bill-to.

const nzd = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" });
const money = (v) => nzd.format(Number(v || 0));

const ISSUER = {
  name: "Grayburn Property Services Ltd",
  gst: "35-746-343",
  address: ["2D Rawhiti Rd, One Tree Hill, Auckland", "PO Box 11-551, Ellerslie"],
  phone: "09-622 3900",
  email: "reception@gps1.co.nz",
  // The bank account deliberately does NOT live here. It belongs to the payment
  // terms band (PaymentTerms.jsx), and a second copy in this file is precisely
  // how the two documents ended up promising different terms in the first place.
};

function Field({ label, children }) {
  if (!children) return null;
  return (
    <tr>
      <td className="pe-3 text-muted align-top" style={{ whiteSpace: "nowrap" }}>{label}</td>
      <td style={{ whiteSpace: "pre-wrap" }}>{children}</td>
    </tr>
  );
}

// The evidence block Vero asked for: proof the work is finished, on the same
// page as the charge (docs/invoice-refactor.md, GPS-CD-004). Robert's framing —
// "like Amazon delivering a box and taking a photo at the front door."
//
// `evidence` comes from GET /claims/:id/invoice-evidence and is optional, so
// every existing caller of InvoiceView renders exactly as before.
function CompletionEvidence({ evidence, photos }) {
  const notes = evidence?.shows_completion ? evidence.completion_notes || [] : [];
  const shots = photos || [];
  if (notes.length === 0 && shots.length === 0) return null;
  return (
    <div className="mt-4">
      {/* The heading never sits alone at the foot of a page — print.css already
          applies that to h1-h6 generally, so this uses one. */}
      <h6 className="fw-bold border-bottom pb-1 mb-2">Completion</h6>
      {/* One row per progress entry that recorded the CLIENT'S closure sign-off.
          Read, never re-typed, so the invoice cannot claim a closure call that
          was never made. The flag is Closure Call Client Signoff, not plain
          Closure Call — the printed line asserts the client signed the work
          off. Positive feedback used to print here too and was removed
          (KS 2026-08-22): praise is not evidence of completion, and it has its
          own reports (services/invoices.js). */}
      {notes.map((n, i) => (
        <div key={i} className="small mb-1">
          <span className="text-muted me-2">{formatDate(n.entry_date)}</span>
          {n.closure_call_client_signoff ? <strong>Closure call (client sign-off).</strong> : null}
          {/* The typed note starts on ITS OWN LINE under the label (KS
              meeting 2026-08-26, job 76518) — inline, a long note read as
              part of the label. */}
          <div style={{ whiteSpace: "pre-wrap" }}>{n.details}</div>
        </div>
      ))}
      {/* TWO ACROSS (Robert, 14 Aug, after seeing three). Three fitted eight
          photographs on one page and that was the wrong trade: these exist to
          PROVE the work is done, so an assessor has to be able to see what is in
          them. A second sheet of paper is cheaper than a photograph nobody can
          read. Two across on A4 is roughly 85mm a picture, about four to a page,
          which is what Karen and Robert first described.

          A flex-wrap was giving four across on a wide screen and three on a
          narrow one, so the printed page depended on the window that printed it;
          an explicit grid does not.

          break-inside is on each TILE, not the block: eight photographs may
          legitimately need a page break, and forbidding it on the whole block
          would push the lot onto a fresh page and leave the invoice half empty.
          What must never happen is a break THROUGH a picture. */}
      {shots.length > 0 ? (
        <div
          className="mt-2"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "8px",
          }}
        >
          {shots.map((p) => (
            <img
              key={p.id}
              src={invoicePhotoUrl(p.id)}
              alt=""
              className="border rounded"
              style={{
                width: "100%",
                aspectRatio: "4 / 3",
                objectFit: "cover",
                breakInside: "avoid",
                pageBreakInside: "avoid",
              }}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function InvoiceQuoteHeader({ text }) {
  if (!text || !String(text).trim()) return null;
  return <div className="mb-3" style={{ whiteSpace: "pre-wrap" }}>{text}</div>;
}

// A Do & Charge invoice's All-details view heads its costing rows with the
// header typed at the build (the additions label). If that is the same words
// as the job's Invoice Quote Header, the paragraph above has already said it.
const sameText = (a, b) => !!a && !!b && String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

function InvoiceView({ invoice, detailed = false, evidence = null, photos = null }) {
  if (!invoice) return null;
  const excess = Number(invoice.excess_amount || 0);
  const totalInclGst = Number(invoice.subtotal || 0) + Number(invoice.gst || 0);
  // "All details" (legacy QuoteReportEx-style invoice): the stamped quote lines
  // replace the summary lines. Only quoted invoices carry them.
  const showDetail = detailed && invoice.detail_lines?.length > 0;

  return (
    <div className="print-document bg-white text-dark p-4" style={{ maxWidth: 820, margin: "0 auto" }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-4">
        {/* Logo and company name on ONE line, exactly as the quote; the address
            sits UNDER them rather than beside the logo (Robert 2026-08-04).
            Beside the logo it pushed the block wide and left the File Life
            stamp stranded in white space. */}
        <div>
          <div className="d-flex align-items-center gap-3">
            <IssuerLogo className="" />
            <div className="fs-4 fw-bold">{ISSUER.name}</div>
          </div>
          <div className="mt-2">
            {ISSUER.address.map((l, i) => <div key={i} className="small text-muted">{l}</div>)}
            <div className="small text-muted">Ph: {ISSUER.phone} · {ISSUER.email}</div>
          </div>
        </div>
        <div className="text-end">
          <div className="fs-3 fw-bold text-uppercase">Tax Invoice</div>
          <div>#{invoice.invoice_no}</div>
          <div className="small fw-semibold">GST: {ISSUER.gst}</div>
          <div className="small text-muted">Date {formatDate(invoice.invoice_date)}</div>
          {invoice.is_final ? <div className="small text-uppercase fw-semibold text-success">Final</div> : null}
          {/* FILE LIFE stamp (legacy — on every invoice): the duration measure,
              or "Job Not Completed"/"Job Not Accepted" while dates are missing.
              Deliberate fixed red — it's the 1.0 stamp, part of the document. */}
          {invoice.file_life ? (
            <div className="mt-2 d-inline-block text-center" style={{ border: "2px solid #c00", color: "#c00", padding: "2px 12px", fontWeight: 700 }}>
              <div style={{ letterSpacing: "0.2em", fontSize: "1.05rem" }}>FILE LIFE</div>
              <div style={{ fontSize: "0.78rem", borderTop: "1px solid #c00", paddingTop: 1 }}>{invoice.file_life}</div>
            </div>
          ) : null}
        </div>
      </div>

      {/* To / Re */}
      <div className="row mb-4">
        <div className="col-6">
          <div className="text-uppercase small text-muted fw-semibold mb-1">To</div>
          {/* THE JOB'S "INVOICE TO" LINES REPLACE THE ADDRESSEE (1.0's
              "Quoteable Invoice To", three lines, kept on the job and typed in
              the side panel; invoices.md 11.1). 1.0's own rule: only when line
              1 says something — then the three lines are the whole TO block
              and the routing contact's name and address stay off the page.
              Attention and email stay, as 1.0 printed ATT separately. On
              76580 the office's lines read "AA Insurance / AA Insurance /
              Callout Emergency Works". */}
          {invoice.invoice_to?.length ? (
            invoice.invoice_to.map((l, i) => (
              <div key={i} className={i === 0 ? "fw-semibold" : "small"}>{l}</div>
            ))
          ) : (
            <div className="fw-semibold">{invoice.bill_to_name || "—"}</div>
          )}
          {invoice.bill_to_attention ? <div className="small">Attn: {invoice.bill_to_attention}</div> : null}
          {!invoice.invoice_to?.length && invoice.bill_to_address ? (
            <div className="small" style={{ whiteSpace: "pre-wrap" }}>{invoice.bill_to_address}</div>
          ) : null}
          {invoice.bill_to_email ? <div className="small text-muted">{invoice.bill_to_email}</div> : null}
        </div>
        <div className="col-6">
          <div className="text-uppercase small text-muted fw-semibold mb-1">Re</div>
          <table className="small"><tbody>
            <Field label="Client">{invoice.client_name}</Field>
            <Field label="Address">{invoice.site_address}</Field>
            <Field label="Your Ref">{invoice.your_ref}</Field>
            <Field label="Insurer">{invoice.insurer_name}</Field>
            <Field label="Claim #">{invoice.insurance_claim_number}</Field>
            <Field label="Our Ref">GPS#{invoice.claim_number}</Field>
          </tbody></table>
        </div>
      </div>

      {/* THE INVOICE QUOTE HEADER (1.0's InvoicesPage box of that name, on
          the job, typed in the side panel; invoices.md 11.2): the paragraph
          above the lines, printed exactly as typed and carrying no amount —
          on 76580, "STRIPOUT REQUEST - CHEMDRY - 02.09.26" and the request
          under it. */}
      <InvoiceQuoteHeader text={invoice.invoice_quote_header} />

      {/* Lines */}
      <table className="table table-sm align-middle">
        <thead>
          <tr style={{ borderBottom: "2px solid #dee2e6" }}>
            <th>Description</th>
            <th className="text-end" style={{ width: 140 }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {showDetail
            ? invoice.detail_lines.filter((l) => !(l.is_header && sameText(l.description, invoice.invoice_quote_header))).map((l, i) =>
                l.is_header ? (
                  <tr key={i}>
                    <td colSpan={2} className="fw-semibold text-uppercase pt-3">{l.description || "—"}</td>
                  </tr>
                ) : (
                  <tr key={i}>
                    <td style={{ whiteSpace: "pre-wrap" }}>{l.description || "—"}</td>
                    <td className="text-end text-nowrap">{Number(l.net_value) ? money(l.net_value) : ""}</td>
                  </tr>
                )
              )
            : /* THE SUMMARISED DOCUMENT (KS 2026-08-28, one shape for every
                 insurer on Standard R&Q): the quotation line carries the
                 QUOTE portion, then a VARIATIONS block (one line per
                 variation) and a PC SUMS block — all built server-side
                 (summary_lines) so the page foots to the Sub Total exactly.
                 This replaced the summary_line + variation_headers pair,
                 which printed the whole subtotal AND the variations again —
                 $20,310.31 of lines over an $18,484.26 Sub Total on job
                 74865. Itemised invoices name variations inline, so they
                 carry no appended block. */
              (invoice.summary_lines || invoice.lines)?.map((l, i) =>
                l.heading ? (
                  <tr key={i}>
                    <td colSpan={2} className="fw-semibold text-uppercase pt-3">{l.heading}</td>
                  </tr>
                ) : (
                  <tr key={i}>
                    <td style={{ whiteSpace: "pre-wrap" }}>{l.description || "—"}</td>
                    <td className="text-end text-nowrap">{money(l.amount)}</td>
                  </tr>
                )
              )}
          {!showDetail && !invoice.summary_lines?.length && (!invoice.lines || invoice.lines.length === 0) ? (
            <tr><td colSpan={2} className="text-center text-muted">No lines.</td></tr>
          ) : null}
        </tbody>
      </table>

      {/* Totals */}
      <div className="d-flex justify-content-end">
        <table style={{ minWidth: 320 }}><tbody>
          <tr>
            <td className="text-muted pe-4">Sub Total</td>
            <td className="text-end">{money(invoice.subtotal)}</td>
          </tr>
          <tr>
            <td className="text-muted pe-4">Plus GST ({Math.round(Number(invoice.gst_rate || 0.15) * 100)}%)</td>
            <td className="text-end">{money(invoice.gst)}</td>
          </tr>
          <tr style={{ borderTop: "1px solid #dee2e6" }}>
            <td className="text-muted pe-4">Total incl GST</td>
            <td className="text-end">{money(totalInclGst)}</td>
          </tr>
          {excess > 0 ? (
            <tr>
              <td className="text-muted pe-4">Less excess</td>
              <td className="text-end">−{money(excess)}</td>
            </tr>
          ) : null}
          <tr className="fs-5 fw-bold" style={{ borderTop: "2px solid #212529" }}>
            <td className="pe-4">TOTAL</td>
            <td className="text-end">{money(invoice.total)}</td>
          </tr>
        </tbody></table>
      </div>

      {invoice.excess_collection_note ? (
        <div className="text-danger small mt-3" style={{ whiteSpace: "pre-line" }}>
          {invoice.excess_collection_note}
        </div>
      ) : null}

      {/* PAYMENT TERMS DIRECTLY UNDER THE TOTALS (KS 2026-09-15, invoice 84271):
          the band used to close the document, which put it on the last page
          under the photographs — an assessor reading the money never saw the
          terms. The invoice ends at its total and its terms; the completion
          evidence follows as the attachment it is. */}
      <PaymentTerms reference={`#${invoice.invoice_no}`} />

      <CompletionEvidence evidence={evidence} photos={photos} />
    </div>
  );
}

export default InvoiceView;
